import { ClearRounded, ExpandMoreRounded } from "@mui/icons-material";
import {
  AutocompleteProps,
  CircularProgress,
  ListSubheader,
  Autocomplete as MuiAutocomplete,
  styled,
  Typography,
} from "@mui/material";
import { createContext, forwardRef, useContext } from "react";
import { FixedSizeList, ListChildComponentProps } from "react-window";

const StyledMuiAutocomplete = styled(MuiAutocomplete)`
  .MuiInputLabel-root {
    color: ${({ theme }) => theme.palette.grey[400]};

    &.Mui-disabled {
      color: ${({ theme }) => theme.palette.text.disabled};
    }
  }

  .MuiChip-root {
    height: 32px;
    background-color: ${({ theme }) => theme.palette.primary.transparent10};
    border-radius: 32px;
    color: ${({ theme }) => theme.palette.primary.main};

    .MuiChip-label {
      font-size: ${({ theme }) => theme.typography.body1.fontSize};
      text-transform: none;
    }

    .MuiSvgIcon-root {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }
` as typeof MuiAutocomplete;

const LISTBOX_PADDING = 10; // px

interface IProps<T> {
  getOptionLabel: (item: T) => string;
  isLoading?: boolean;
}

const WindowedAutocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>(
  props: IProps<T> & AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>
) => {
  const { isLoading, ...other } = props;

  const renderRow = ({ data, index, style }: ListChildComponentProps) => {
    const dataSet = data[index];
    const inlineStyle = {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
    };

    if (dataSet.hasOwnProperty("group")) {
      return (
        <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
          {dataSet.group}
        </ListSubheader>
      );
    }

    const { key, ...optionProps } = dataSet[0];
    const optionLabel = props.getOptionLabel(dataSet[1]);

    return (
      <Typography key={key} component="li" {...optionProps} noWrap style={inlineStyle}>
        {optionLabel}
      </Typography>
    );
  };

  const OuterElementContext = createContext({});

  const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  });

  const ListBoxComponent = forwardRef((props: any, ref: any) => {
    const { children, ...other } = props;
    const itemData: any = [];
    children.forEach((item: any) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    });

    const itemCount = itemData.length;
    const itemSize: number = 36;

    const getChildSize = () => {
      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a: number, b: number) => a + b, 0);
    };

    return (
      <div ref={ref} {...other}>
        <OuterElementContext.Provider value={other}>
          <FixedSizeList
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            itemData={itemData}
            itemCount={itemCount}
            itemSize={itemSize}
            outerElementType={OuterElementType}
            innerElementType="ul"
            overscanCount={5}
          >
            {renderRow}
          </FixedSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  });

  return (
    <StyledMuiAutocomplete
      blurOnSelect
      handleHomeEndKeys
      disableListWrap
      clearIcon={<ClearRounded sx={{ fontSize: "20px" }} />}
      ListboxComponent={ListBoxComponent}
      renderOption={(props, option, state) => [props, option, state.index] as React.ReactNode} // We unpack this object in the react-window FixedSizeList component to give us access to "option"
      renderGroup={(params) => params as any}
      popupIcon={
        isLoading ? (
          <CircularProgress size={20} sx={{ color: "text.disabled" }} />
        ) : props.disabled ? null : (
          <ExpandMoreRounded sx={{ color: "primary.main", fontSize: "24px" }} />
        )
      }
      {...other}
    />
  );
};

export { WindowedAutocomplete };
